import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Download, DollarSign, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { sr } from "../../lib/sr";

export default function MetaStatements() {
  const nav = useNavigate();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async (m) => {
    setLoading(true);
    try { setData(await sr.metaStatementsDashboard(m || month)); }
    catch (e) {
      if (e?.response?.status === 403) { toast.error("Staff only"); nav("/app/meta-ads"); }
      else toast.error("Could not load statements");
    }
    setLoading(false);
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);
  const downloadCsv = async () => { try { const blob = await sr.metaStatementsCsv(month); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `meta-statements-${month}.csv`; a.click(); URL.revokeObjectURL(url); } catch { toast.error("Could not download CSV"); } };

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "20px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ fontSize: 24, margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
          <DollarSign size={22} color="#067647" /> Meta Ads statements
        </h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input type="month" className="sr-input" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="statements-month" />
          <button className="sr-btn sr-btn-sm" onClick={() => load(month)} data-testid="statements-refresh"><RefreshCw size={13} /> Refresh</button>
          <button className="sr-btn sr-btn-sm" onClick={downloadCsv} data-testid="statements-csv"><Download size={13} /> CSV</button>
        </div>
      </div>
      <p className="sr-muted" style={{ fontSize: 13 }}>Every client's Meta total for the month (staff view — raw media + 15% margin) and whether the invoice was emailed.</p>

      {loading ? <Loader2 className="sr-spin" /> : !data || data.rows.length === 0 ? (
        <div className="sr-card" data-testid="statements-empty">No clients with Meta connections yet.</div>
      ) : (
        <div className="sr-card" data-testid="statements-table">
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12 }}>
            <Metric label="Clients" v={data.client_count} />
            <Metric label="Managed media (billed)" v={`$${data.totals.media_spend.toLocaleString()}`} />
            <Metric label="Raw media" v={`$${data.totals.raw_media_spend.toLocaleString()}`} />
            <Metric label="Total billable" v={`$${data.totals.total.toLocaleString()}`} />
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted)", fontSize: 12 }}>
                <th style={{ padding: "6px 4px" }}>Client</th><th>Raw media</th><th>Billed media</th><th>Module</th><th>Total</th><th>Invoice</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.tenant_id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`statement-row-${r.tenant_id}`}>
                  <td style={{ padding: "8px 4px", fontWeight: 600 }}>{r.business}</td>
                  <td>${r.raw_media_spend.toLocaleString()}</td>
                  <td>${r.media_spend.toLocaleString()}</td>
                  <td>${r.module_fee}</td>
                  <td style={{ fontWeight: 700 }}>${r.total.toLocaleString()}</td>
                  <td>
                    {r.emailed ? <span className="sr-badge" style={{ color: "#067647" }}>Emailed</span>
                      : r.finalized ? <span className="sr-badge" style={{ color: "#B8791B" }}>Finalized</span>
                        : <span className="sr-badge" style={{ color: "var(--muted)" }}>Not finalized</span>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {r.invoice_id && (
                      <button className="sr-btn sr-btn-sm sr-btn-primary" disabled={busy === r.tenant_id}
                        onClick={async () => { setBusy(r.tenant_id); try { const x = await sr.metaEmailInvoice(r.invoice_id); x.ok ? toast.success(`Emailed ${r.business}`) : toast.info("Not sent"); await load(month); } catch (e) { toast.error(e?.response?.data?.detail || "Could not email"); } setBusy(""); }}
                        data-testid={`statement-email-${r.tenant_id}`}>
                        {busy === r.tenant_id ? <Loader2 size={12} className="sr-spin" /> : <><Send size={12} /> {r.emailed ? "Resend" : "Email"}</>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Metric = ({ label, v }) => (
  <div><div className="sr-muted" style={{ fontSize: 12 }}>{label}</div><div style={{ fontSize: 22, fontWeight: 800 }}>{v}</div></div>
);
